/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import { ArrowLeft, Download, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import {
  convertTo12Hour,
  getTimeInAmPmFormat,
} from "../../../utils/formatDateTime";
import { formatDate } from "../../../utils/helpers";

const PAYMENT_BADGE_THEME = {
  CASH: "bg-amber-100 text-amber-700 ring-amber-200",
  UPI_QR: "bg-emerald-100 text-emerald-700 ring-emerald-200",
};

const AdminInvoicesDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInvoiceDetails = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/invoices/${id}`,
      );
      setInvoice(res.data);
    } catch (error) {
      console.error("Failed to load invoice details", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchInvoiceDetails();
  }, [id]);

  const handleDownloadPDF = () => {
    window.open(
      `${process.env.REACT_APP_API_URL}/invoices/pdf/${id}`,
      "_blank",
    );
  };

  const cleanAddress = (address) => {
    if (!address) return null;
    return address.replace(/^,\s*/, "");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        Loading invoice…
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        Invoice not found
      </div>
    );
  }

  return (
    <div className="min-h-[90vh] bg-gradient-to-br from-[#FFF7F3] to-gray-50">
      <div className="px-4 py-3">
        <Breadcrumb title1="Invoices" title2={invoice.invoiceNumber} />
      </div>

      <div className="px-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Invoices
          </button>

          <div className="flex gap-3">
            <button
              onClick={handleDownloadPDF}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md bg-[#f0653a] text-white hover:bg-[#c0512e]"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </button>
          </div>
        </div>

        {/* Invoice Card */}
        <div className="bg-white border border-[#F7BFAD] rounded-2xl p-6 space-y-6">
          {/* Top Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InfoBlock label="Invoice No" value={invoice.invoiceNumber} />
            <InfoBlock
              label="Invoice Date"
              value={formatDate(invoice.invoiceDate)}
            />
            <div>
              <p className="text-xs text-gray-500 mb-1">Payment Mode</p>
              <span
                className={`inline-flex items-center justify-center px-4 h-7 text-xs font-semibold rounded-full ring-1 ${PAYMENT_BADGE_THEME[invoice.paymentMode]}`}
              >
                {invoice.paymentMode === "UPI_QR" ? "UPI" : invoice.paymentMode}
              </span>
            </div>
          </div>

          {/* Customer, Branch & Appointment */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <DetailsBox title="Customer">
              <p className="font-medium">{invoice.customerSnapshot.name}</p>
              <p className="text-sm text-gray-600">
                {invoice.customerSnapshot.phoneNumber}
              </p>
              {invoice.customerSnapshot.address && (
                <p className="text-sm text-gray-600">
                  {cleanAddress(invoice.customerSnapshot.address)}
                </p>
              )}
            </DetailsBox>
            {invoice.appointmentSnapshot?.date && (
              <DetailsBox title="Appointment">
                <p className="font-medium">
                  {formatDate(invoice.appointmentSnapshot?.date)}
                </p>

                <p className="text-sm text-gray-600">
                  {invoice.appointmentSnapshot?.startTime} –{" "}
                  {invoice.appointmentSnapshot?.endTime}
                </p>

                {invoice.staffSnapshot?.name && (
                  <p className="text-sm text-gray-600 mt-1">
                    <span className="font-medium text-gray-700">Staff:</span>{" "}
                    {invoice.staffSnapshot.name}
                  </p>
                )}
              </DetailsBox>
            )}

            <DetailsBox title="Branch">
              <p className="font-medium">{invoice.branchSnapshot.name}</p>
              {invoice.branchSnapshot.address && (
                <p className="text-sm text-gray-600">
                  {invoice.branchSnapshot.address}
                </p>
              )}
            </DetailsBox>
          </div>

          {/* Items */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Invoice Items
            </h3>

            <div className="border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2">Item</th>
                    <th className="text-center px-4 py-2">Qty</th>
                    <th className="text-right px-4 py-2">Unit Price</th>
                    <th className="text-right px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-4 py-2">{item.name}</td>
                      <td className="px-4 py-2 text-center">{item.quantity}</td>
                      <td className="px-4 py-2 text-right">
                        ₹{item.unitPrice}
                      </td>
                      <td className="px-4 py-2 text-right font-medium">
                        ₹{item.totalPrice}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Total */}
          <div className="flex justify-end border-t pt-4">
            <div className="text-right">
              <p className="text-sm text-gray-500">
                Total Amount (GST Included)
              </p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{invoice.totalAmount}
              </p>
            </div>
          </div>

          {/* Note */}
          {invoice.note && (
            <p className="text-xs text-gray-500 text-center">{invoice.note}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoBlock = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="font-medium text-gray-900">{value}</p>
  </div>
);

const DetailsBox = ({ title, children }) => (
  <div className="border rounded-xl p-4">
    <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
    <div className="space-y-1">{children}</div>
  </div>
);

export default AdminInvoicesDetails;
